import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { api, HAR_BASE, HAR_CID } from "../lib/api";
import Seo from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        ...form,
        consent_marketing: consent,
        consent_sms: consent && !!form.phone,
        consent_source: "website:/contact",
      });
      toast.success("Thanks! An agent will be in touch within 1 business hour.");
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
    } catch {
      toast.error("Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-stone-50 min-h-screen" data-testid="contact-page">
      <Seo
        title="Contact Us"
        description="Contact Gens Realty, LLC — Houston's boutique residential real estate brokerage. Call (832) 741-4662 or send a message; we respond within 1 business hour."
        path="/contact"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-20 pb-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#C9A961]" />
          <div className="eyebrow text-[#0A0A0A]">Contact</div>
        </div>
        <h1 className="font-display text-6xl lg:text-8xl leading-[0.92] tracking-tight text-[#0A0A0A] font-light">
          Let's <span className="serif-italic text-[#E63946]">talk.</span>
        </h1>
        <p className="text-[#555] mt-6 max-w-xl text-lg font-light">
          Buying, selling, or just exploring — drop us a line and we'll get back within one business hour.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            className="bg-white border border-gray-200 rounded-2xl p-7 lg:p-10 space-y-5"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full name">
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-stone-50 rounded-md px-4 py-3 text-sm focus:outline-none border border-gray-200 focus:border-[#E63946]"
                  data-testid="contact-name"
                />
              </Field>
              <Field label="Email">
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-stone-50 rounded-md px-4 py-3 text-sm focus:outline-none border border-gray-200 focus:border-[#E63946]"
                  data-testid="contact-email"
                />
              </Field>
            </div>
            <Field label="Phone (optional)">
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-stone-50 rounded-md px-4 py-3 text-sm focus:outline-none border border-gray-200 focus:border-[#E63946]"
                data-testid="contact-phone"
              />
            </Field>
            <Field label="How can we help?">
              <textarea
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Tell us a bit about what you're looking for..."
                className="w-full bg-stone-50 rounded-md px-4 py-3 text-sm focus:outline-none border border-gray-200 focus:border-[#E63946] resize-none"
                data-testid="contact-message"
              />
            </Field>

            {/* Opt-in — TCPA + A2P 10DLC compliant */}
            <label
              className="flex items-start gap-3 rounded-md border border-gray-200 bg-stone-50/60 px-4 py-3 cursor-pointer select-none"
              data-testid="contact-consent-label"
            >
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#E63946]"
                data-testid="contact-consent-checkbox"
              />
              <span className="text-xs leading-relaxed text-gray-600">
                By checking this box, I agree to receive marketing and transactional
                communications from <strong>Gens Realty, LLC</strong> at the email and
                phone number provided, including via <strong>SMS/text message</strong>{" "}
                and email. Message frequency varies. Message &amp; data rates may apply.
                Consent is <em>not</em> a condition of purchase. Reply <strong>STOP</strong>{" "}
                to opt out or <strong>HELP</strong> for help at any time. See our{" "}
                <Link
                  to="/privacy"
                  className="text-[#E63946] underline underline-offset-2"
                  data-testid="contact-consent-privacy-link"
                >
                  Privacy Policy
                </Link>
                {" "}for details. Your information will not be shared with third parties
                for their marketing purposes.
              </span>
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-[#E63946] hover:bg-[#c9313d] text-white px-8 py-3.5 rounded-md font-semibold text-sm disabled:opacity-50 transition-colors"
              data-testid="contact-submit"
            >
              {submitting ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-5 space-y-5">
          <Card icon={<Phone className="w-5 h-5" />} title="Phone">
            <a href="tel:8327414662" className="text-[#1A1A1A] hover:text-[#E63946]">(832) 741-4662</a>
            <div className="text-sm text-gray-500 mt-1">Direct line · Mon–Sat</div>
          </Card>
          <Card icon={<Mail className="w-5 h-5" />} title="Email">
            <a href="mailto:PJoseph@gensrealty.com" className="text-[#1A1A1A] hover:text-[#E63946]">PJoseph@gensrealty.com</a>
            <div className="text-sm text-gray-500 mt-1">We respond within 1 hr</div>
          </Card>
          <Card icon={<MapPin className="w-5 h-5" />} title="Office">
            <div>14911 Heritage Wood Drive</div>
            <div>Houston, TX 77082</div>
          </Card>
          <Card icon={<Clock className="w-5 h-5" />} title="Hours">
            <div>Mon – Fri · 9 AM – 7 PM</div>
            <div>Saturday · 10 AM – 5 PM</div>
            <div className="text-sm text-gray-500 mt-1">Sundays by appointment</div>
          </Card>
        </aside>
      </section>

      {/* HAR Lead Form */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-3">
          HAR · Quick Request
        </div>
        <h2 className="font-display text-3xl font-semibold tracking-tight mb-4">
          Request more information
        </h2>
        <p className="text-[#555] max-w-2xl mb-6">
          Prefer the HAR quick-request form? Use the secure form below — it goes straight to our inbox.
        </p>
        <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
          <iframe
            title="HAR lead form"
            src={`${HAR_BASE}/leadform?sitetype=aws&cid=${HAR_CID}`}
            className="w-full"
            style={{ height: "720px", minHeight: "640px", border: "0" }}
            data-testid="har-leadform"
            loading="lazy"
          />
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

function Card({ icon, title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-[#E63946]/10 text-[#E63946] flex items-center justify-center">
          {icon}
        </div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">{title}</div>
      </div>
      <div className="text-[#1A1A1A] font-medium">{children}</div>
    </div>
  );
}
